import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Chapter, Prose, TheoryBox, MTQuote, Glossary, Term } from "@/components/mt/MTBox";
import { NoteImage } from "@/components/mt/NoteImage";
import { ScaleExplorer } from "@/components/mt/ScaleExplorer";
import { NextUp } from "@/components/Callouts";

export default function MusicTheory24() {
  const { lesson, course, accent } = findLesson("music-theory", "24");
  return (
    <LessonShell
      course={course}
      courseSlug="music-theory"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/music-theory/MT24-Parallel-Modes-Animated.mp4"
        caption="Parallel modes — same root C, alter individual degrees"
        hint="≈ 40 sec · watch first"
      />
      <Chapter number="24" eyebrow="Music Theory · Chapter Twenty-four" title="Same root — different notes">
        <Prose>
          <p>
            The previous chapter showed how relative scales keep the notes constant and
            change the root. This chapter does the opposite. <strong>Parallel scales</strong>{" "}
            keep the <em>root</em> constant and change the <em>notes</em>. C major and C
            minor are parallel scales: they share the note C as their home, but the seven
            notes building each scale are different.
          </p>
          <p>
            Parallel thinking is a composer's secret weapon. It lets you borrow a single
            chord, or a phrase, from a scale that shares your home note but lives in a
            different mood. The song stays anchored in C, but for a moment colours from C
            Mixolydian or C Dorian leak in, and the music takes on a flavour you couldn't
            reach with relative thinking alone.
          </p>
          <MTQuote>
            In the parallel case, the notes are going to change, but the root will stay the
            same.
          </MTQuote>
        </Prose>
      </Chapter>

      <Chapter eyebrow="The seven parallel modes of C" title="One root · seven different stories">
        <Prose>
          <p>
            All seven modes can be built from the same root. Built on C, they look like
            this:
          </p>
          <ul className="space-y-1 text-[14px]">
            <li><strong>C Ionian</strong> = C D E F G A B (the major scale)</li>
            <li><strong>C Dorian</strong> = C D E♭ F G A B♭ (minor + raised 6)</li>
            <li><strong>C Phrygian</strong> = C D♭ E♭ F G A♭ B♭ (minor + flat 2)</li>
            <li><strong>C Lydian</strong> = C D E F♯ G A B (major + sharp 4)</li>
            <li><strong>C Mixolydian</strong> = C D E F G A B♭ (major + flat 7)</li>
            <li><strong>C Aeolian</strong> = C D E♭ F G A♭ B♭ (the natural minor)</li>
            <li><strong>C Locrian</strong> = C D♭ E♭ F G♭ A♭ B♭ (minor + flat 2 + flat 5)</li>
          </ul>
          <p>
            Notice that C is the home in <em>every</em> case. Only the seven notes around
            it shift. Parallel thinking forces you to feel the difference between the modes
            directly — because the root note doesn't move, only the colour notes do.
          </p>
        </Prose>

        <ScaleExplorer tonic="C" scales={["major", "dorian", "phrygian", "lydian", "mixolydian", "naturalMinor", "locrian"]} />
      </Chapter>

      <Chapter eyebrow="Parallel major and minor" title="The most familiar parallel relationship">
        <Prose>
          <p>
            The classic parallel pair is the major scale and its parallel minor. C major and
            C natural minor share the note C as their root, but three notes differ — the
            3rd, 6th, and 7th all flatten when you switch from major to minor.
          </p>
          <ul className="space-y-1 text-[14px]">
            <li>C major: C D <strong>E</strong> F G <strong>A</strong> <strong>B</strong></li>
            <li>C minor: C D <strong>E♭</strong> F G <strong>A♭</strong> <strong>B♭</strong></li>
          </ul>
          <p>
            That difference — flatten the 3, 6 and 7 — is the whole journey from major to
            minor. Songs that move between parallel major and minor (rather than relative
            major and minor) keep the same emotional centre but flip the mood. The Beatles'
            <em> Norwegian Wood</em> does it in the open — the verse sits in E, the middle
            section drops into E minor — and countless cinematic scores use the same switch
            to darken the room without modulating to a new key.
          </p>
        </Prose>

        <TheoryBox label="Parallel-minor formula">
          To turn any major scale into its parallel minor: <strong>flatten the 3rd, 6th and
          7th</strong>. The root, 2nd, 4th and 5th stay the same.
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="Borrowed chords" title="Steal from the parallel · stay at home">
        <Prose>
          <p>
            The most powerful use of parallel thinking is <strong>borrowed chords</strong>.
            Imagine you're writing a song in G major. The default chords come from G major:
            G, C, D, Em, Am, Bm. But you can briefly visit G's parallel modes and steal a
            chord from them.
          </p>
          <ul className="space-y-1 text-[14px]">
            <li>From G <strong>Mixolydian</strong>, you can borrow F major (the ♭VII chord). It lifts the song with a country / Beatles flavour.</li>
            <li>From G <strong>Dorian</strong> — minor, but with a bright natural 6 — you can borrow a Gm6, or the major IV (C) sitting over a minor tonic. It's the sound of a minor key refusing to be gloomy.</li>
            <li>From G <strong>natural minor</strong>, you can borrow B♭ (the ♭III), E♭ (the ♭VI), or G minor itself for a sudden darkness.</li>
          </ul>
          <p>
            The Beatles do this gloriously in <em>Hey Jude</em> — the whole song is in F
            major, but the famous "na na na" outro hammers an E♭ chord (♭VII) borrowed
            straight from F Mixolydian. It's still F. It just got bigger. <em>With a Little
            Help from My Friends</em> uses the same trick.
          </p>
        </Prose>

        <MTQuote>
          You're keeping the key centre, let's say G or E or F or whatever, but altering the
          notes or borrowing notes.
        </MTQuote>
      </Chapter>

      <Chapter eyebrow="The six interesting parallels" title="What each one gives you">
        <Prose>
          <p>
            Once you know all seven parallel modes of your home root, you have a palette
            of six "alternative" scales to colour with. Each one offers something specific:
          </p>
          <ul className="space-y-1 text-[14px]">
            <li><strong>Parallel Mixolydian</strong> — flat 7. Uplifting, brotherhood, anthemic. The "Hey Jude" colour.</li>
            <li><strong>Parallel Lydian</strong> — sharp 4. Dreamy, magical, otherworldly. The "wonder" colour from film scores.</li>
            <li><strong>Parallel Dorian</strong> — minor with raised 6. Hopeful sadness; jazz; <em>Scarborough Fair</em>.</li>
            <li><strong>Parallel Phrygian</strong> — flat 2. Spanish, flamenco, metal, ominous.</li>
            <li><strong>Parallel natural minor</strong> — flat 3, 6, 7. The classic minor.</li>
            <li><strong>Parallel Locrian</strong> — flat 2 + flat 5. Avoid using as a tonic; useful for one-bar half-diminished colour.</li>
          </ul>
        </Prose>

        <Glossary>
          <Term name="Parallel scale" def="A scale that shares its root with another but uses different notes. Same home, new neighbours." />
          <Term name="Borrowed chord" def="A chord taken from the parallel scale of the current key. Lets you visit a different mode without leaving home." />
          <Term name="Modal interchange" def="The technical name for borrowed chords — the practice of pulling chords from any parallel mode for colour." />
        </Glossary>
      </Chapter>

      <Chapter eyebrow="A composer's trick" title="Major-with-flat-6 — the half-borrowed gem">
        <Prose>
          <p>
            Here's a personal favourite the chapter wants you to know. Stay in major, but
            permanently flatten the 6th. So you're in C major (C D E F G A B), but you
            change the A to A♭. The new scale is C D E F G A♭ B — neither pure major nor
            pure minor.
          </p>
          <p>
            That's a hybrid borrowed straight from <strong>C Harmonic Major</strong> (or you
            can think of it as major with a borrowed flat 6 from C minor). It gives you a
            major chord on the I, a major 3rd that sings, and an A♭ that pulls down with
            longing. Sting uses it. Stevie Wonder uses it. Once you hear it, it's everywhere.
          </p>
        </Prose>

        <TheoryBox label="The major-with-♭6 hybrid">
          A favourite trick: take a major scale and flatten <em>only</em> the 6th. The 3rd
          stays major. The result is "happy with one tear in its eye" — a hybrid scale
          that combines major's brightness with minor's longing.
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="What's next" title="Putting both lenses together">
        <Prose>
          <p>
            We now have two ways to see the same set of modes — relatively (sharing notes,
            different roots) and parallelly (sharing root, different notes). Neither view
            is "more correct"; they're two ways of looking at the same harmonic landscape.
            The next chapter puts both lenses side by side, so you can switch between them
            on demand — and use whichever frame is more useful for the question you're
            asking.
          </p>
        </Prose>
        <NoteImage
          src="/notes/music-theory/finals/24a-modes-from-root-C.png"
          caption="The same modes from the parallel perspective — all built on C. C ionian (no alterations), C lydian (♯4), C mixolydian (♭7), C aeolian (♭3 ♭6 ♭7), C dorian (♭3 ♭7), C phrygian (♭2 ♭3 ♭6 ♭7), C locrian (♭2 ♭3 ♭5 ♭6 ♭7). Each mode = the major scale plus a unique combination of alterations."
        />
      </Chapter>

      <NextUp
        to="/lesson/music-theory-25"
        label="Chapter 25 · Relative vs Parallel Scales"
        why="Two views, one map. Side-by-side comparisons of the relative and parallel approaches — and when each one is the right tool for the job."
      />
    </LessonShell>
  );
}
